import { useState, useEffect, useRef, useCallback } from 'react';
import logger from '../../utils/logger';
import { API, axiosInstance } from './dashboardApi';

// Simple cache for recommendations (5 minute TTL)
const recommendationsCache = {
  data: null,
  timestamp: null,
  assessmentId: null,
  TTL: 5 * 60 * 1000 // 5 minutes
};

// Cache for dashboard stats to persist across navigation
const statsCache = {
  stats: null,
  timestamp: null,
  TTL: 5 * 60 * 1000 // 5 minutes
};

export const useDashboardData = () => {
  const [assessmentsLoading, setAssessmentsLoading] = useState(true);
  const [recommendationsLoading, setRecommendationsLoading] = useState(true);

  const [recentAssessments, setRecentAssessments] = useState([]);
  const [topRecommendations, setTopRecommendations] = useState([]);
  // Initialize with cached stats if available, otherwise defaults
  const [dashboardData, setDashboardData] = useState(() => {
    const now = Date.now();
    if (statsCache.stats && statsCache.timestamp && (now - statsCache.timestamp) < statsCache.TTL) {
      logger.log('✅ [Dashboard] Initializing with cached stats');
      return {
        stats: statsCache.stats,
        industry_comparison: {}
      };
    }
    return {
      stats: {
        total_assessments: 0,
        total_savings: 0,
        total_recommendations: 0,
        avg_payback: 0
      },
      industry_comparison: {}
    };
  });

  const [assessmentsError, setAssessmentsError] = useState(null);
  const [recommendationsError, setRecommendationsError] = useState(null);
  
  const isMountedRef = useRef(true);
  const hasLoadedDataRef = useRef(false); // Track if we've successfully loaded data
  const fetchAssessmentsRef = useRef(null); // Store fetch function for refresh
  const fetchRecommendationsRef = useRef(null); // Store recommendations fetch function

  useEffect(() => {
    const fetchRecommendations = async (assessmentsData = null) => {
      const startTime = performance.now();
      logger.log(
        '🟡 [Dashboard] Starting recommendations fetch at:',
        new Date().toISOString()
      );

      try {
        let assessments = assessmentsData;
        if (!assessments) {
          logger.log('🔄 [Dashboard] Fetching assessments for recommendations...');
          const assessmentsResponse = await axiosInstance.get(`${API}/user/assessments`);
          assessments = assessmentsResponse.data?.assessments || [];
        } else {
          logger.log('✅ [Dashboard] Using already-fetched assessments data');
        }

        if (assessments.length === 0) {
          logger.log('ℹ️ [Dashboard] No assessments found, no recommendations to show');
          setTopRecommendations([]);
          setRecommendationsLoading(false);
          return;
        }

        const mostRecentAssessment = assessments[0];
        const assessmentId = mostRecentAssessment.id || mostRecentAssessment.assessment_id;

        logger.log(
          `📊 [Dashboard] Fetching recommendations for assessment: ${assessmentId}`
        );

        // Check cache first
        const now = Date.now();
        if (
          recommendationsCache.data &&
          recommendationsCache.assessmentId === assessmentId &&
          recommendationsCache.timestamp &&
          (now - recommendationsCache.timestamp) < recommendationsCache.TTL
        ) {
          logger.log('✅ [Dashboard] Using cached recommendations');
          setTopRecommendations(recommendationsCache.data);
          setRecommendationsLoading(false);
          setRecommendationsError(null);
          return;
        }

        // Reduced timeout from 45s to 20s - if it takes longer, there's a problem
        // Also use 'free' report type for dashboard (only quick-wins, faster response)
        const response = await axiosInstance.get(
          `${API}/assessments/${assessmentId}/recommendations?report_type=free`,
          { 
            timeout: 20000 // Reduced to 20s - should be fast for stored recommendations
          }
        );

        const endTime = performance.now();
        logger.log(
          `✅ [Dashboard] Recommendations loaded in ${(endTime - startTime).toFixed(0)}ms`
        );

        let quickWinRecommendations = response.data?.recommendations || [];
        const summary = response.data?.summary || {};

        logger.log(`📊 [Dashboard] Raw API response summary:`, summary);
        logger.log(`📊 [Dashboard] Raw recommendations count: ${quickWinRecommendations.length}`);

        // Additional frontend filter: Ensure only recommendations with payback <= 2 years
        // This is a safety measure in case backend filtering isn't perfect
        quickWinRecommendations = quickWinRecommendations.filter(rec => {
          const payback = parseFloat(rec.payback_years || rec.simple_payback_years || 999);
          return payback <= 2.0;
        });

        logger.log(
          `✅ [Dashboard] Loaded ${quickWinRecommendations.length} quick-win recommendations (<= 2yr payback) from assessment ${assessmentId}`
        );
        logger.log(
          `📊 [Dashboard] Summary from API: Total: ${summary.total_recommendations}, Quick-wins: ${summary.quick_win_recommendations || quickWinRecommendations.length}, Avg Payback: ${summary.average_payback}, Total Savings: ${summary.total_annual_savings}, Showing: ${quickWinRecommendations.length}`
        );

        // Always update state - React will handle unmounted component updates safely
        // Cache the recommendations
        recommendationsCache.data = quickWinRecommendations;
        recommendationsCache.assessmentId = assessmentId;
        recommendationsCache.timestamp = Date.now();

        logger.log(`📊 [Dashboard] Setting recommendations state with ${quickWinRecommendations.length} items`);
        setTopRecommendations(quickWinRecommendations);
        setRecommendationsError(null);
        logger.log(`📊 [Dashboard] Recommendations state updated successfully`);

        const quickWinCount = quickWinRecommendations.length;
        
        // Use summary data from API which includes ALL recommendations (not just quick-wins)
        // The API summary.total_recommendations includes all recommendations
        // The API summary.average_payback is calculated from all recommendations
        const totalRecommendations = summary.total_recommendations || 0;
        const avgPayback = summary.average_payback || 0;

        logger.log(`📊 [Dashboard] Using API summary stats (from ALL recommendations):`, {
          quickWinCount,
          totalAllRecommendations: summary.total_recommendations,
          avgPaybackFromAPI: summary.average_payback,
          summaryTotalSavings: summary.total_annual_savings,
          note: 'Stats cards show ALL recommendations, but dashboard section shows only quick-wins (<= 2yr payback)'
        });

        // Update stats with recommendation data, preserving assessment stats
        setDashboardData((prev) => {
          const prevStats = prev?.stats || {};
          const updatedStats = {
            ...prevStats,
            // Use total recommendations from API (all recommendations, not just quick-wins)
            total_recommendations: totalRecommendations,
            // Use average payback from API (calculated from all recommendations)
            avg_payback: avgPayback,
            // CRITICAL: Preserve assessment savings - don't overwrite with quick-win savings
            // Total Savings should represent savings from ALL assessments, not just quick-wins
            // Quick-win savings ($103,500) is a subset of total assessment savings ($90,000)
            // We keep the assessment savings as the source of truth for "Total Savings"
            total_savings: prevStats.total_savings || 0 // Keep assessment savings, don't overwrite
          };
          
          // Cache the updated stats to persist across navigation
          statsCache.stats = updatedStats;
          statsCache.timestamp = Date.now();
          
          logger.log(`📊 [Dashboard] Updating stats with recommendations:`, {
            prevStats,
            updatedStats,
            assessmentSavings: prevStats.total_savings,
            note: 'Stats cards show ALL recommendations count and average payback from API summary'
          });
          
          return {
            ...(prev || { stats: {}, industry_comparison: {} }),
            stats: updatedStats,
            industry_comparison: prev?.industry_comparison || {}
          };
        });
        
        // Force a re-render to ensure stats are displayed
        logger.log(`📊 [Dashboard] Stats update complete - total_recommendations (all): ${totalRecommendations}, avg_payback (all): ${avgPayback.toFixed(2)}`);

        logger.log(
          `📊 [Dashboard] Stats updated: ${totalRecommendations} total recommendations, ${avgPayback.toFixed(1)}yr avg payback (from all recommendations)`
        );
      } catch (error) {
        const endTime = performance.now();
        logger.error(
          `❌ [Dashboard] Recommendations failed after ${(endTime - startTime).toFixed(
            0
          )}ms`,
          error
        );
        logger.error('Full error details:', error);

        // Don't clear existing recommendations if we have them
        if (topRecommendations && topRecommendations.length > 0) {
          logger.log(
            '✅ Recommendations loaded successfully despite error in stats update'
          );
          setRecommendationsLoading(false);
          return;
        }

        // Only set error and clear if we don't have existing recommendations
        if (error.code === 'ECONNABORTED') {
          setRecommendationsError(
            'Request timeout - Please refresh the page to try again'
          );
        } else if (error.response?.status === 404) {
          setRecommendationsError(
            'No recommendations found - Complete an assessment first'
          );
        } else if (error.response?.status === 403) {
          setRecommendationsError('Access denied to recommendations');
        } else {
          setRecommendationsError(
            'Unable to load recommendations - Please try refreshing the page'
          );
        }
        
        // Always update state - React handles unmounted updates
        setTopRecommendations([]);
      } finally {
        // Always set loading to false
        setRecommendationsLoading(false);
      }
    };

    // Store fetchRecommendations in ref for refresh
    fetchRecommendationsRef.current = fetchRecommendations;

    fetchAssessmentsRef.current = async () => {
      const startTime = performance.now();
      logger.log(
        '🔵 [Dashboard] Starting assessments fetch at:',
        new Date().toISOString()
      );

      try {
        const response = await axiosInstance.get(`${API}/user/assessments`, {
          timeout: 30000 // Increased back to 30s - 20s was too short for some cases
        });
        const endTime = performance.now();
        logger.log(
          `✅ [Dashboard] Assessments loaded in ${(endTime - startTime).toFixed(0)}ms`
        );

        const assessments = response.data?.assessments || [];
        
        logger.log(`📊 [Dashboard] Received ${assessments.length} assessments from API`);

        // Always update state - React will handle unmounted component updates safely
        // The ref check was too strict and causing data to not be set
        setRecentAssessments(assessments);
        hasLoadedDataRef.current = true; // Mark that we've successfully loaded data

        logger.log(`📊 [Dashboard] Setting state with ${assessments.length} assessments`);

        // Calculate assessment-only stats (don't include recommendation stats)
        const assessmentStats = {
          total_assessments: assessments.length,
          total_savings: assessments.reduce(
            (sum, a) => sum + (a.estimated_savings || 0),
            0
          )
        };

        logger.log(`📊 [Dashboard] Calculated assessment stats:`, assessmentStats);
        
        // Use functional update to preserve existing recommendation stats
        // This ensures recommendations stats (total_recommendations, avg_payback) are never overwritten
        setDashboardData((prev) => {
          const prevStats = prev?.stats || {};
          
          // Check cached stats as fallback if current state doesn't have recommendation stats
          const cachedStats = statsCache.stats;
          const hasCachedRecommendations = cachedStats && 
            (cachedStats.total_recommendations > 0 || cachedStats.avg_payback > 0);
          
          // CRITICAL: Never overwrite recommendation stats if they exist (either in state or cache)
          // Only set to 0 if they truly don't exist anywhere
          const recommendationCount = prevStats.total_recommendations !== undefined && prevStats.total_recommendations > 0
            ? prevStats.total_recommendations
            : (hasCachedRecommendations ? cachedStats.total_recommendations : 0);
          
          const recommendationPayback = prevStats.avg_payback !== undefined && prevStats.avg_payback > 0
            ? prevStats.avg_payback
            : (hasCachedRecommendations ? cachedStats.avg_payback : 0);
          
          const updatedStats = {
            ...prevStats,
            // Update assessment-specific stats
            total_assessments: assessmentStats.total_assessments,
            // CRITICAL: Always use assessment savings as the source of truth for "Total Savings"
            // Assessment savings represents savings from ALL assessments ($90,000)
            // This should not be overwritten by quick-win savings (which is a subset)
            total_savings: assessmentStats.total_savings, // Always use assessment savings
            // CRITICAL: Preserve recommendation stats - use cached values if current state is 0
            // This prevents stats from disappearing during navigation/refresh
            total_recommendations: recommendationCount,
            avg_payback: recommendationPayback
          };
          
          logger.log(`📊 [Dashboard] Merging assessment stats, preserving recommendations:`, {
            prevStats,
            cachedStats,
            assessmentStats,
            updatedStats,
            preservingFromState: prevStats.total_recommendations !== undefined && prevStats.total_recommendations > 0,
            preservingFromCache: hasCachedRecommendations
          });
          
          return {
            ...(prev || { stats: {}, industry_comparison: {} }),
            stats: updatedStats,
            industry_comparison: prev?.industry_comparison || {}
          };
        });
        logger.log(`📊 [Dashboard] Assessments stats updated (preserving recommendation stats)`);

        // ✅ OPTIMIZATION: Start fetching recommendations in parallel immediately
        // Don't wait for assessments to finish - start as soon as we have assessment ID
        if (assessments.length > 0) {
          const mostRecentAssessment = assessments[0];
          const assessmentId = mostRecentAssessment.id || mostRecentAssessment.assessment_id;
          
          // Start recommendations fetch immediately (non-blocking)
          fetchRecommendations(assessments).catch(err => {
            // Error already handled in fetchRecommendations
            logger.error('Recommendations fetch failed:', err);
          });
        } else {
          // No assessments - set recommendations loading to false immediately
          setRecommendationsLoading(false);
          setTopRecommendations([]);
        }
      } catch (error) {
        const endTime = performance.now();
        logger.error(
          `❌ [Dashboard] Assessments failed after ${(endTime - startTime).toFixed(
            0
          )}ms`
        );
        logger.error('Error details:', error);

        // ✅ CRITICAL FIX: Don't clear existing data on timeout/error
        // If we already have data loaded, keep it and just show the error
        if (hasLoadedDataRef.current) {
          logger.warn('⚠️ [Dashboard] Error occurred but keeping existing data (already loaded)');
          // Just set the error, don't clear data
          if (error.code === 'ECONNABORTED') {
            setAssessmentsError('Request timeout - Using previously loaded data');
          } else {
            setAssessmentsError('Failed to refresh - Using previously loaded data');
          }
          // Still set loading to false so UI updates
          setAssessmentsLoading(false);
          // Don't clear data or stop recommendations loading - they should continue
          return;
        }
        
        // If we don't have loaded data yet, this is the initial load failure
        logger.error('❌ [Dashboard] Initial load failed - no data to preserve');

        // Only clear data if we haven't successfully loaded any data yet
        if (error.code === 'ECONNABORTED') {
          setAssessmentsError('Request timeout - Backend is taking too long to respond');
        } else if (error.response?.status === 401) {
          setAssessmentsError('Authentication failed - Please log in again');
        } else if (error.response?.status === 500) {
          setAssessmentsError('Server error - Please try again later');
        } else if (!error.response) {
          setAssessmentsError('Network error - Cannot reach backend server');
        } else {
          setAssessmentsError(`Failed to load assessments: ${error.message}`);
        }

        // Only clear data if we haven't successfully loaded any data yet
        if (!hasLoadedDataRef.current) {
          // Only set to zeros if we truly have no data
          // Don't overwrite if dashboardData already has values
          const currentStats = dashboardData?.stats;
          const hasAnyData = currentStats && (
            currentStats.total_assessments > 0 ||
            currentStats.total_savings > 0 ||
            currentStats.total_recommendations > 0
          );
          
          if (!hasAnyData) {
            logger.log('⚠️ [Dashboard] No existing data, setting to empty state');
            setDashboardData({
              stats: {
                total_assessments: 0,
                total_savings: 0,
                total_recommendations: 0,
                avg_payback: 0
              },
              industry_comparison: {}
            });
          } else {
            logger.log('✅ [Dashboard] Keeping existing data despite error');
          }
          
          // If assessments failed and we have no data, stop recommendations loading
          if (!hasAnyData) {
            setRecommendationsLoading(false);
            setTopRecommendations([]);
          }
        }
      } finally {
        setAssessmentsLoading(false);
      }
    };

    // Only fetch once on mount
    if (isMountedRef.current) {
      fetchAssessmentsRef.current();
    }
    
    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  // Refresh function to reload dashboard data
  const refreshDashboard = useCallback(() => {
    logger.log('🔄 [Dashboard] Manual refresh triggered');
    // Clear recommendations cache to force fresh data
    recommendationsCache.data = null;
    recommendationsCache.timestamp = null;
    recommendationsCache.assessmentId = null;
    // CRITICAL: Don't clear stats cache - preserve stats during refresh to prevent showing 0
    // Stats cache will be updated when new data loads
    // Reset loading states
    setAssessmentsLoading(true);
    setRecommendationsLoading(true);
    // Fetch fresh data
    if (fetchAssessmentsRef.current) {
      fetchAssessmentsRef.current();
    }
  }, []);

  return {
    assessmentsLoading,
    recommendationsLoading,
    recentAssessments,
    topRecommendations,
    dashboardData,
    assessmentsError,
    recommendationsError,
    refreshDashboard
  };
};


